import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Columns3, GripVertical } from "lucide-react";
import { COLUMN_DEFS, DEFAULT_COLUMN_CONFIG } from "./columns";

const DEFS = Object.fromEntries(COLUMN_DEFS.map((d) => [d.key, d]));

const SortableRow = ({ id, visible, onToggle }) => {
  const def = DEFS[id];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`column-row-${id}`}
      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${isDragging ? "relative z-10 bg-[#242424]" : "hover:bg-[#1c1c1c]"}`}>
      <button {...attributes} {...listeners} data-testid={`column-drag-${id}`}
        className="cursor-grab text-[#555] hover:text-white active:cursor-grabbing">
        <GripVertical size={13} />
      </button>
      <Checkbox checked={visible} disabled={def.locked} onCheckedChange={() => onToggle(id)}
        data-testid={`column-toggle-${id}`}
        className="border-[#333] data-[state=checked]:border-[#4F8EF7] data-[state=checked]:bg-[#4F8EF7]" />
      <span className="text-[12px] text-[#e5e5e5]">{def.label}</span>
      {def.locked && <span className="ml-auto text-[9.5px] text-[#555]">always on</span>}
    </div>
  );
};

export const ColumnManager = ({ config, onChange, compact = false }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const toggle = (key) => onChange(config.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = config.findIndex((c) => c.key === active.id);
    const newI = config.findIndex((c) => c.key === over.id);
    onChange(arrayMove(config, oldI, newI));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {compact ? (
          <button data-testid="columns-button" title="Show, hide & reorder columns"
            className="flex h-6 w-6 items-center justify-center rounded-md text-[#6b6b6b] transition-colors hover:bg-[#1f1f1f] hover:text-white">
            <Columns3 size={13} />
          </button>
        ) : (
          <button data-testid="columns-button"
            className="flex h-8 items-center gap-1.5 rounded-md border border-[#222] bg-[#111] px-2.5 text-[12px] text-[#A3A3A3] transition-colors hover:border-[#333] hover:text-white">
            <Columns3 size={13} /> Columns
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 border-[#242424] bg-[#161616] p-2 text-white" data-testid="columns-popover">
        <div className="px-2 pb-1.5 pt-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
          Show, hide & drag to reorder
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={config.map((c) => c.key)} strategy={verticalListSortingStrategy}>
            {config.map((c) => (
              <SortableRow key={c.key} id={c.key} visible={c.visible} onToggle={toggle} />
            ))}
          </SortableContext>
        </DndContext>
        <button onClick={() => onChange(DEFAULT_COLUMN_CONFIG)} data-testid="columns-reset"
          className="mt-1.5 w-full rounded-lg border border-[#242424] py-1.5 text-[11px] text-[#A3A3A3] transition-colors hover:bg-[#1c1c1c] hover:text-white">
          Reset to default
        </button>
      </PopoverContent>
    </Popover>
  );
};
